<template lang="pug">
.sms-whatsapp-message-template
  v-row(no-gutters)
    v-col.mr-4
      .title.mb-8
        .text-md.font-medium {{ $t('whatsapp_message_template') }}
        .text-sm.neutral700--text.font-normal {{ $t('whatsapp_review_message_template_description') }}
      .form
        v-form(ref="form")
          //- Review
          .mb-1
            .text-sm.font-semibold For review
            .text-xs This template will be used if there is not appointment or consultation data on the selected patient
          v-row.mb-4(no-gutters)
            v-col.mb-8(cols="5" :xl="templateCols - 1")
              v-card.message-preview-border.my-2.round-xl(outlined)
                v-row(no-gutters justify="center")
                  v-col.pa-4(cols="8")
                    div.text-sm.font-normal.neutral700--text(style="white-space: pre-wrap;" v-html="formatInput(formattedMessage('review'))")
              .d-flex.justify-end.mt-4
                v-btn.button-md(color="basic800" dark depressed :isLoading="isLoading" @click="onEditTemplate('review')") {{ $t('sms_whatsapp_message_template_edit_template') }}
          //- Appointment
          .mb-1
            .text-sm.font-semibold For appointment
            .text-xs This template will be used if there is appointment data on the selected patient
          v-row.mb-4(no-gutters)
            v-col.mb-8(cols="5" :xl="templateCols - 1")
              v-card.message-preview-border.my-2.round-xl(outlined)
                v-row(no-gutters justify="center")
                  v-col.pa-4(cols="8")
                    div.text-sm.font-normal.neutral700--text(style="white-space: pre-wrap;" v-html="formatInput(formattedMessage('appointment'))")
              .d-flex.justify-end.mt-4
                v-btn.button-md(color="basic800" dark depressed :isLoading="isLoading" @click="onEditTemplate('appointment')") {{ $t('sms_whatsapp_message_template_edit_template') }}
          //- CMS
          .mb-1
            .text-sm.font-semibold For clinic management system
            .text-xs This template will be used if there is consultation data on the selected patient
          v-row(no-gutters)
            v-col.mb-8(cols="5" :xl="templateCols - 1")
              v-card.message-preview-border.my-2.round-xl(outlined)
                v-row(no-gutters justify="center")
                  v-col.pa-4(cols="8")
                    div.text-sm.font-normal.neutral700--text(style="white-space: pre-wrap;" v-html="formatInput(formattedMessage('cms'))")
              .d-flex.justify-end.mt-4
                v-btn.button-md(color="basic800" dark depressed :isLoading="isLoading" @click="onEditTemplate('cms')") {{ $t('sms_whatsapp_message_template_edit_template') }}

  .dialogs
    edit-message-template(v-if="openEditMessageDialog" :open.sync="openEditMessageDialog" :templateMessage="selectedTemplate" :templateType="selectedTemplateType" @saving="saveData($event)")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import EditMessageTemplate from '@/components/review/dialogs/EditMessageTemplate.vue'

export default {
  name: 'SmsWhatsappTemplateSetup',
  components: {
    EditMessageTemplate
  },
  data () {
    return {
      isLoading: false,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
      },
      currentTemplateReview: null,
      currentTemplateAppointment: null,
      currentTemplateCms: null,
      openEditMessageDialog: false,
      templateCols: 5,
      selectedTemplateType: null,
      selectedTemplate: null
    }
  },
  props: {
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  mounted() {
    this.mapData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      reviewSettings: 'review/getSettings',
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    reviewLink () {
      if (process.env.VUE_APP_ENV === 'prod') {
        return `https://review.yezza.co/review/${this.company.slug}`
      }
      return `https://review-test.yezza.co/review/${this.company.slug}`
    },
    payloadKey () {
      const templateTypeKey = {
        review: 'review_sms_template',
        appointment: 'appointment_sms_template',
        cms: 'cms_sms_template',
      }

      return templateTypeKey[this.selectedTemplateType] || null
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    mapData() {
      this.currentTemplateReview = this.reviewSettings.review_sms_template
      this.currentTemplateAppointment = this.reviewSettings.appointment_sms_template
      this.currentTemplateCms = this.reviewSettings.cms_sms_template
    },
    formattedMessage (templateType) {
      const templateTypes = {
        review: this.currentTemplateReview,
        appointment: this.currentTemplateAppointment,
        cms: this.currentTemplateCms,
      }

      let message = templateTypes[templateType]
      if (message) {
        message = message.split('{{name}}').join('Sarah Danial')
        message = message.split('{{review_link}}').join(this.reviewLink)
      }

      return message
    }, 
    formatInput(string) {
      string = String(string);
      
      //-----> for bold text
      let bold = string.split(" ").map((word) =>
      {
        if (/\*/.test(word) && !/^\**$/.test(word))
          if (/(^(~\*)|^(_\*)|^\*)(.*)(\*$|(\*_)$|(\*\W)$)/.test(word))
            return word.replace(/\*([^\r\n]+)\*/g, "<b>$1</b>")
          else if (/[_*~]\*(.*)\*[~*_]/.test(word))
            return word.replace(/\*([^*][^\r\n]+[^*])\*/g, "<b>$1</b>");
        return word;
      }).join(" ");

      //-----> for italic text 
      let italic = bold.split(" ").map((word) =>
      {
        if (/_/.test(word) && !/^_+$/.test(word))
        if (/(^~_|^_<.*>|^_\*|^_)(.*)(_$|(_<.*>)$|(_\*)$|(_\W)$)/.test(word))
          return word.replace(/_([^\r\n]+)_/g, "<i>$1</i>");
        else if (/(~|_|\*|<.*>)_(.*)_(<.*>|\*|~|_)/.test(word))
          return word.replace(/_([^_][^\r\n]+[^_])_/g, "<i>$1</i>");
        return word;
      }).join(" ");
      
      //----->for strikethrough/deleted text 
      let strikethrough = italic.split(" ").map((word) =>
      {
        if (/~/.test(word) && !/^~+$/.test(word))
          if (/(^_~|^~<.*>|^\*~|^~)(.*)(~$|(~_)$|(~<.*>)$|(~\*)$|(~\W)$)/.test(word))
            return word.replace(/~([^\r\n]+)~/g, "<del>$1</del>")
          else if (/(~|_|\*|<.*>)~(.*)~(<.*>|\*|~|_)/.test(word))
            return word.replace(/~([^~][^\r\n]+[^~])~/g, "<del>$1</del>");
        return word;
      }).join(" ");
      
      
      
      //-----> for underlined text
      var underline = strikethrough.split(" ").map(function(word)
      {
        if (/%/.test(word) && !/^%+$/.test(word))
          if (/(^_%|^~%|^%<.*>|^\*%|^%)(.*)(%$|(%~)$|(%_)$|(%<.*>)$|(%\*)$|%\W+$)/.test(word))
            return word.replace(/%([^\r\n]+)%/g, "<u>$1</u>")
          else if (/(%|~|_|\*|<.*>)%(.*)%(<.*>|\*|~|%|_)/.test(word))
            return word.replace(/%([^%][^\r\n]+[^%])%/g, "<u>$1</u>");
        return word;
      }).join(" ");
      
      
      
      //-----> for monospace text
      var monospace = underline.split(" ").map(function(word)
      {
        if (/```/.test(word))
          if (/```(.|\n|\r)*(.+)(.|\n|\r])*```/.test(word)) 
            return word.replace(/```((.|\n|\r)*(.*)(\r|\n|.)*)```/g, `<span style="font-family: 'Courier New', Courier, monospace">$1</span>`);
        return word;
      }).join(" ");
        
      var formatted = monospace;
      
      //-----> for bold sentence formatting
      if (/\*(.*)\*/.test(formatted))
        formatted = formatted.replace(/(^|[\s_>~%`])\*([^\s\r\n]+[^*\n\r]+[^\s\r\n]+)\*([\W_\r\n\s]|$)/g, "$1<b>$2</b>$3");


      //-----> for italic sentence formatting 
      if (/_(.*)_/.test(formatted))
        formatted = formatted.replace(/(^|[\s>~%`*])_([^\s\r\n]+[^_\n\r]+[^\s\n\r]+)_([\W_\s\r\n]|$)/g, "$1<i>$2</i>$3");
      
      
      //-----> for strikethrough sentence formatting 
      if (/~(.*)~/.test(formatted))
        formatted = formatted.replace(/(^|[\s>%`*_])~([^\s\r\n]+[^~\r\n]+[^\r\n\s]+)~([\W_\s\r\n]|$)/g, "$1<del>$2</del>$3");
      
      
      //-----> for underline sentence formatting 
      if (/%(.*)%/.test(formatted))
        formatted = formatted.replace(/(^|[\s>~`*_])%([^\s\r\n]+[^%\r\n]+[^\r\n\s]+)%([\W_\s\r\n]|$)/g, "$1<u>$2</u>$3");
    
    
      //-----> for monospace sentence formatting
      var counter = 0;
      if (/```/.test(formatted))
      {
        var i = 0;
        formatted = formatted.split(" ");
        formatted = formatted.map(function(word)
        {
          //tempArr[i+1] handles case where input is like this: ``` ``` with no characters between back ticks
          if (!/<code>/.test(word))
            if (/```/.test(word) && formatted[i+1] != "```")
            {
              i++; counter++;
              if (counter % 2 != 0) 
                return word.replace("```", "<code>");
              else
                return word.replace("```", "</code>");
            } i++; 
          return word;
        }).join(" ");

        //handles case where there are no closing back ticks
        if (counter % 2 == 1)
        {
          formatted = formatted.split(" ");
          for (i = formatted.length-1; i >= 0; i--)
            if (/<code>/.test(formatted[i]))
            {
              formatted[i] = formatted[i].replace(/<code>/, "```");
              break;
            }
          formatted = formatted.join(" ");
        }
      }
      return formatted;
    },
    saveData (message) {
      this.isLoading = true
      this.$axios.patch(`/reviews/${this.company.slug}`, { [this.payloadKey]: message || null })
        .then(({ data }) => {
          this.openEditMessageDialog = false
          const updateData = data[this.payloadKey]
          if (this.selectedTemplateType === 'review') this.currentTemplateReview = updateData
          if (this.selectedTemplateType === 'appointment') this.currentTemplateAppointment = updateData
          if (this.selectedTemplateType === 'cms') this.currentTemplateCms = updateData
          this.$store.commit('review/setSettings', data)
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onEditTemplate (templateType) {
      const templateTypes = {
        review: this.currentTemplateReview,
        appointment: this.currentTemplateAppointment,
        cms: this.currentTemplateCms,
      }

      this.selectedTemplate = templateTypes[templateType]
      this.selectedTemplateType = templateType
      this.openEditMessageDialog = true
    }
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.no-padding-x {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.selected-button {
  background: #EEEEEE !important;
  border-radius: 16px;
}
::v-deep svg.v-icon.notranslate.v-data-table-header__icon {
  height: 8px !important;
  width: 8px !important;
  margin-left: 8px;
}
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
.pre-wrap {
  white-space: pre-wrap;
}
.sticky {
  top: 1%;
  position: sticky;
}
.message-preview-border {
  border: var(--v-basic500) solid 1px;
}
</style>